<template>
  <aside class="preview">
    <div class="card pcard dark">
      <div class="ph">
        <h3>Готовая ссылка</h3>
        <span class="status" :class="status"><i></i>{{ statusLabel }}</span>
      </div>
      <div class="url-box">
        <template v-if="plain && url">
          <span class="base">{{ url }}</span>
        </template>
        <template v-else-if="base">
          <span class="base">{{ base }}</span>
          <template v-for="(param, index) in params" :key="param.key">
            <span :class="index === 0 && !base.includes('?') ? 'q' : 'amp'">{{ index === 0 && !base.includes('?') ? '?' : '&' }}</span>
            <span class="k">{{ param.key }}</span>=<span class="v" :title="param.fromEvent ? 'из события' : ''">{{ param.value }}</span>
          </template>
        </template>
        <span v-else class="q">Соберите ссылку</span>
      </div>
      <div class="copy-row">
        <button type="button" class="btn primary" @click="$emit('copy')">Скопировать <span class="kbd">{{ copyHint }}</span></button>
        <button type="button" class="btn icon-btn" title="Сократить" @click="$emit('shorten')">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M10 14a4 4 0 006 0l2-2a4 4 0 00-6-6l-1 1"/><path d="M14 10a4 4 0 00-6 0l-2 2a4 4 0 006 6l1-1"/></svg>
        </button>
        <button type="button" class="btn icon-btn" title="Ссылка для Telegram" @click="$emit('telegram')">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M21 4L3 11l6 2 2 6 4-5 5 4z"/></svg>
        </button>
      </div>
      <div v-if="chips.length" class="chips">
        <span v-for="(chip, index) in chips" :key="chip" class="chip" :class="{ olive: index === 0 }">{{ chip }}</span>
      </div>
      <div class="summary">
        <div class="row"><span>Кампания</span><b>{{ campaignText || '—' }}</b></div>
        <div class="row"><span>Событие</span><b>{{ eventText || '—' }}</b></div>
        <div class="row">
          <span>Реестр</span>
          <b :class="registryReady ? 'ok' : 'wait'">{{ registryReady ? 'запись создастся при копировании' : 'без события запись не создаётся' }}</b>
        </div>
      </div>
    </div>

    <div class="card pcard">
      <div class="qr">
        <canvas v-show="url" ref="canvasRef" class="code" width="100" height="100"></canvas>
        <div v-if="!url" class="code placeholder"></div>
        <div class="qt">
          <b>QR-код ссылки</b>
          <template v-if="url">Для офлайн-материалов и презентаций</template>
          <template v-else>Соберите ссылку — появится QR</template>
          <div class="qr-actions">
            <button type="button" class="btn sm" :disabled="!url" @click="downloadPng">Скачать PNG</button>
            <button v-if="canCopyImage" type="button" class="btn sm" :disabled="!url" @click="copyImage">Скопировать</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="recent.length" class="card pcard">
      <div class="ph">
        <h3>Недавние</h3>
        <button type="button" class="btn sm ghost" @click="wipeRecent">Очистить</button>
      </div>
      <div class="recent">
        <div v-for="item in recent" :key="item.url + item.ts" class="item">
          <span class="u" :title="item.url">{{ formatUrlForDisplay(item.url, 48) }}</span>
          <span class="t">{{ formatRecentLabel(item.ts) }}</span>
          <button type="button" title="Скопировать" @click="copyRecent(item.url)">⧉</button>
        </div>
      </div>
    </div>
  </aside>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import QRCode from 'qrcode'
import { clearRecent, formatRecentLabel, formatUrlForDisplay, pushRecent, readRecent } from './utm-utils'

const props = defineProps({
  url: { type: String, default: '' },
  base: { type: String, default: '' },
  params: { type: Array, default: () => [] },
  plain: { type: Boolean, default: false },
  status: { type: String, default: 'url' },
  chips: { type: Array, default: () => [] },
  campaignText: { type: String, default: '' },
  eventText: { type: String, default: '' },
  registryReady: { type: Boolean, default: false },
})

defineEmits(['copy', 'shorten', 'telegram'])

const toast = useToast()
const canvasRef = ref(null)
const recent = ref(readRecent())
const canCopyImage = typeof ClipboardItem !== 'undefined'
const copyHint = /Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘C' : 'Ctrl C'

const statusLabel = computed(() => {
  if (props.status === 'ready') return 'готова'
  if (props.status === 'event') return 'выберите событие'
  return 'нужен адрес'
})

watch(() => props.url, async (url) => {
  await nextTick()
  if (!url || !canvasRef.value) return
  try {
    await QRCode.toCanvas(canvasRef.value, url, { width: 100, margin: 1 })
  } catch {
    /* QR не критичен для сборки ссылки */
  }
}, { immediate: true })

function remember(url) {
  if (!url) return
  recent.value = pushRecent(url)
}

function wipeRecent() {
  clearRecent()
  recent.value = []
}

async function copyRecent(url) {
  try {
    await navigator.clipboard.writeText(url)
    toast.add({ severity: 'success', summary: 'Ссылка скопирована', life: 2000 })
  } catch {
    toast.add({ severity: 'error', summary: 'Не удалось скопировать', life: 2500 })
  }
}

function downloadPng() {
  if (!canvasRef.value || !props.url) return
  const link = document.createElement('a')
  link.href = canvasRef.value.toDataURL('image/png')
  link.download = 'utm-qr.png'
  link.click()
}

async function copyImage() {
  if (!canvasRef.value || !props.url || !canCopyImage) return
  try {
    const blob = await new Promise((resolve) => canvasRef.value.toBlob(resolve, 'image/png'))
    if (!blob) return
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
    toast.add({ severity: 'success', summary: 'QR скопирован', life: 2000 })
  } catch {
    toast.add({ severity: 'error', summary: 'Браузер не дал скопировать картинку', life: 2500 })
  }
}

defineExpose({ remember })
</script>

<style scoped>
.preview {
  --border: #e6e8ec;
  --text: #14181f;
  --muted: #6b7383;
  --muted-2: #98a0ae;
  --accent: #676B52;
  --accent-soft: #eef0e6;
  --mono: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  --shadow: 0 1px 2px rgba(16, 24, 40, .04), 0 8px 24px -12px rgba(16, 24, 40, .12);
  position: sticky;
  top: 20px;
  display: grid;
  gap: 14px;
  min-width: 0;
}
.card { background: #fff; border: 1px solid var(--border); border-radius: 16px; box-shadow: var(--shadow); }
.pcard { padding: 18px; }
.ph { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
.ph h3 { margin: 0; font-size: 13px; font-weight: 600; }
.status {
  margin-left: auto; font-size: 11px; font-weight: 600; border-radius: 6px; padding: 3px 7px;
  display: inline-flex; align-items: center; gap: 5px;
}
.status i { width: 5px; height: 5px; border-radius: 50%; display: block; }
.status.ready { color: #2f7d4f; background: #eaf5ee; }
.status.ready i { background: #2f7d4f; }
.status.url, .status.event { color: #8a6a2f; background: #f8f1e3; }
.status.url i, .status.event i { background: #c49a45; }
.url-box {
  background: #fbfbfc; border: 1px solid var(--border); border-radius: 12px; padding: 12px;
  font-family: var(--mono); font-size: 12.5px; line-height: 1.65; word-break: break-word; max-height: 190px; overflow: auto;
}
.url-box .q, .url-box .amp { color: var(--muted-2); }
.btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px; border-radius: 10px;
  border: 1px solid #d5d9e0; background: #fff; padding: 9px 14px; font-size: 13px; font-weight: 500;
  color: var(--text); cursor: pointer; font-family: inherit;
}
.btn:hover { border-color: #b9bfc9; background: #fbfbfc; }
.btn:disabled { opacity: .45; cursor: default; }
.btn.sm { padding: 6px 10px; font-size: 12px; border-radius: 8px; }
.btn.ghost { border-color: transparent; background: transparent; color: var(--muted); margin-left: auto; }
.btn.ghost:hover { background: #eceef2; color: var(--text); }
.icon { width: 16px; height: 16px; display: block; }
.copy-row { display: flex; gap: 8px; margin-top: 12px; }
.copy-row .btn.primary { flex: 1 1 auto; height: 42px; }
.icon-btn { width: 42px; height: 42px; padding: 0; }
.kbd { font-family: var(--mono); font-size: 10.5px; border: 1px solid rgba(255,255,255,.35); border-radius: 4px; padding: 1px 4px; opacity: .8; }
.summary { display: grid; gap: 8px; margin-top: 14px; padding-top: 14px; border-top: 1px solid #eef0f3; }
.summary .row { display: flex; align-items: flex-start; gap: 8px; font-size: 12.5px; }
.summary .row span { color: var(--muted-2); width: 84px; flex: 0 0 auto; }
.summary .row b { font-weight: 500; }
.summary .ok { color: #2f7d4f; }
.summary .wait { color: var(--muted); font-weight: 500; }
.chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }
.chip { font-size: 11.5px; background: #f3f4f7; border-radius: 999px; padding: 4px 9px; color: #4b5361; }
.chip.olive { background: var(--accent-soft); color: var(--accent); }
.qr { display: flex; gap: 14px; align-items: center; }
.qr .code { width: 100px; height: 100px; border-radius: 10px; border: 1px solid var(--border); flex: 0 0 auto; }
.qr .placeholder { background: repeating-conic-gradient(#14181f 0 25%, #fff 0 50%) 0 0 / 12px 12px; opacity: .18; }
.qr .qt { font-size: 12.5px; color: var(--muted); line-height: 1.5; }
.qr .qt b { display: block; color: var(--text); font-weight: 500; margin-bottom: 4px; }
.qr-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
.recent { display: grid; gap: 2px; }
.recent .item { display: flex; align-items: center; gap: 10px; padding: 9px 8px; border-radius: 9px; }
.recent .item:hover { background: #f6f7f9; }
.recent .u { font-family: var(--mono); font-size: 11.5px; color: var(--muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1 1 auto; }
.recent .t { font-size: 11px; color: var(--muted-2); flex: 0 0 auto; }
.recent button { border: 0; background: transparent; color: var(--muted-2); cursor: pointer; font-size: 14px; }

.pcard.dark { background: #191c19; border-color: #23261f; color: #f2f3ee; padding: 20px; }
.pcard.dark .ph h3 { color: #f2f3ee; }
.pcard.dark .status.ready { background: rgba(122, 163, 116, .16); color: #9fce94; }
.pcard.dark .status.ready i { background: #9fce94; }
.pcard.dark .status.url, .pcard.dark .status.event { background: rgba(196, 154, 69, .16); color: #e6c98a; }
.pcard.dark .url-box { background: #11130f; border-color: #2a2e26; color: #d7dbcd; }
.pcard.dark .url-box .v { color: #c3d49a; font-weight: 600; }
.pcard.dark .url-box .k { color: #8e9880; }
.pcard.dark .url-box .q, .pcard.dark .url-box .amp { color: #6c7563; }
.pcard.dark .btn { background: transparent; border-color: #34392e; color: #e6e8df; }
.pcard.dark .btn:hover { background: #23261f; border-color: #444a3b; }
.pcard.dark .btn.primary { background: #8d9670; border-color: #8d9670; color: #14170f; font-weight: 600; }
.pcard.dark .btn.primary:hover { background: #9aa37d; border-color: #9aa37d; }
.pcard.dark .kbd { border-color: rgba(20, 23, 15, .35); }
.pcard.dark .summary { border-top-color: #2a2e26; }
.pcard.dark .summary .row span { color: #8e9880; }
.pcard.dark .summary .ok { color: #9fce94; }
.pcard.dark .summary .wait { color: #c7ccbd; }
.pcard.dark .chip { background: #242821; color: #c7ccbd; }
.pcard.dark .chip.olive { background: #2c3325; color: #c3d49a; }

@media (max-width: 1180px) {
  .preview { position: static; }
}
@media (max-width: 760px) {
  .copy-row .btn, .icon-btn, .btn.sm, .recent button { min-height: 44px; }
  .icon-btn { width: 44px; }
}
</style>
